import type { Url } from '@wwf/sdk';

const UNREACHABLE_STATUSES = [403, 404, 410];

const IMAGE_EXTENSIONS = new Set([
    'avif',
    'bmp',
    'gif',
    'heic',
    'heif',
    'ico',
    'jpeg',
    'jpg',
    'png',
    'svg',
    'webp',
]);

const AUDIO_EXTENSIONS = new Set(['aac', 'm4a', 'mp3', 'ogg', 'wav']);

const VIDEO_EXTENSIONS = new Set(['avi', 'mkv', 'mov', 'mp4', 'webm']);

const DOCUMENT_EXTENSIONS = new Set([
    'doc',
    'docx',
    'odp',
    'ods',
    'odt',
    'pdf',
    'ppt',
    'pptx',
    'txt',
    'xls',
    'xlsx',
]);

export type UrlProbeKind = 'image' | 'audio' | 'video' | 'document' | 'unknown';

export const getUrlProbeKind = (url: string): UrlProbeKind => {
    try {
        const ext = new URL(url).pathname
            .split('/')
            .pop()
            ?.split('.')
            .pop()
            ?.toLowerCase();
        if (!ext) {
            return 'unknown';
        }
        if (IMAGE_EXTENSIONS.has(ext)) {
            return 'image';
        }
        if (AUDIO_EXTENSIONS.has(ext)) {
            return 'audio';
        }
        if (VIDEO_EXTENSIONS.has(ext)) {
            return 'video';
        }
        if (DOCUMENT_EXTENSIONS.has(ext)) {
            return 'document';
        }
        return 'unknown';
    } catch {
        return 'unknown';
    }
};

const isUnreachableStatus = (status: number): boolean =>
    UNREACHABLE_STATUSES.includes(status);

const probeUrlWithFetch = async (url: Url): Promise<boolean> => {
    const response = await fetch(url, { method: 'HEAD' });
    if (response.ok) {
        return true;
    }
    if (response.status === 405 || response.status === 501) {
        const getResponse = await fetch(url, {
            method: 'GET',
            headers: { Range: 'bytes=0-0' },
        });
        return getResponse.ok;
    }
    return !isUnreachableStatus(response.status);
};

const probeUrlWithTimedLoad = (
    attach: (
        onSuccess: () => void,
        onFailure: () => void
    ) => { cleanup?: () => void },
    timeoutMs = 15_000
): Promise<boolean> =>
    new Promise(resolve => {
        let settled = false;
        let cleanup: (() => void) | undefined;
        const finish = (reachable: boolean) => {
            if (settled) {
                return;
            }
            settled = true;
            clearTimeout(timer);
            cleanup?.();
            resolve(reachable);
        };
        const timer = setTimeout(() => finish(true), timeoutMs);
        cleanup = attach(
            () => finish(true),
            () => finish(false)
        ).cleanup;
    });

const probeUrlWithImage = (url: Url, timeoutMs?: number): Promise<boolean> =>
    probeUrlWithTimedLoad((onSuccess, onFailure) => {
        const img = new Image();
        img.onload = onSuccess;
        img.onerror = onFailure;
        img.src = url;
        return {
            cleanup: () => {
                img.src = '';
            },
        };
    }, timeoutMs);

const probeUrlWithAudio = (url: Url, timeoutMs?: number): Promise<boolean> =>
    probeUrlWithTimedLoad((onSuccess, onFailure) => {
        const audio = new Audio();
        audio.onloadedmetadata = onSuccess;
        audio.onerror = onFailure;
        audio.preload = 'metadata';
        audio.src = url;
        return {
            cleanup: () => {
                audio.src = '';
            },
        };
    }, timeoutMs);

const probeUrlWithVideo = (url: Url, timeoutMs?: number): Promise<boolean> =>
    probeUrlWithTimedLoad((onSuccess, onFailure) => {
        const video = document.createElement('video');
        video.onloadedmetadata = onSuccess;
        video.onerror = onFailure;
        video.preload = 'metadata';
        video.src = url;
        return {
            cleanup: () => {
                video.src = '';
                video.remove();
            },
        };
    }, timeoutMs);

/**
 * When fetch is blocked (CORS), use a media element suited to the file extension.
 * Documents and extension-less URLs cannot be probed cross-origin; returns true (assume ok).
 */
const probeUrlWithoutFetch = async (
    url: Url,
    kind: UrlProbeKind
): Promise<boolean> => {
    switch (kind) {
        case 'image':
            return probeUrlWithImage(url);
        case 'audio':
            return probeUrlWithAudio(url);
        case 'video':
            return probeUrlWithVideo(url);
        case 'document':
        case 'unknown':
            return true;
    }
};

/**
 * Returns false when the URL is clearly unusable (403/404/410).
 * Uses fetch when CORS allows (any file type), otherwise media-element probes for
 * image/audio/video. Cross-origin documents cannot be verified from JS alone.
 */
export const isResourceUrlReachable = async (url: Url): Promise<boolean> => {
    try {
        return await probeUrlWithFetch(url);
    } catch {
        return probeUrlWithoutFetch(url, getUrlProbeKind(url));
    }
};
